import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { ChevronLeftIcon, ChevronRightIcon } from '../../../components/ui/Icons';

export const ReferenceSidebarSplitter: React.FC<{
  panelWidth: number;
  minWidth: number;
  maxWidth: number;
  resizing: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  label?: string;
}> = ({ panelWidth, minWidth, maxWidth, resizing, onMouseDown, label = 'Resize sidebar' }) => {
  const { colors } = useTheme();
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-valuenow={panelWidth}
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
      aria-label={label}
      title="Drag to resize"
      onMouseDown={onMouseDown}
      className={cn(
        'group flex w-1.5 shrink-0 cursor-col-resize items-center justify-center',
        'hover:bg-sky-500/20 active:bg-sky-500/30',
        resizing && 'bg-sky-500/30',
      )}
    >
      <div
        className={cn(
          'h-12 w-0.5 rounded-full opacity-50 group-hover:opacity-100',
          colors.textMuted,
        )}
      />
    </div>
  );
};

export const ReferenceSidebarCollapseButton: React.FC<{
  collapsed: boolean;
  onToggle: () => void;
  className?: string;
  /** Which edge the panel sits on. Controls chevron direction. Default `left`. */
  side?: 'left' | 'right';
}> = ({ collapsed, onToggle, className, side = 'left' }) => {
  const { colors } = useTheme();
  const expandIcon =
    side === 'right' ? (
      <ChevronLeftIcon className="h-4 w-4" />
    ) : (
      <ChevronRightIcon className="h-4 w-4" />
    );
  const collapseIcon =
    side === 'right' ? (
      <ChevronRightIcon className="h-4 w-4" />
    ) : (
      <ChevronLeftIcon className="h-4 w-4" />
    );
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition',
        colors.textMuted,
        colors.interactiveHover,
        className,
      )}
      title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      aria-expanded={!collapsed}
    >
      {collapsed ? expandIcon : collapseIcon}
    </button>
  );
};
